import React, { useState, useEffect,useRef } from "react";
import { useHistory } from "react-router";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "../../../../axios-order";
import CryptoJS from "crypto-js";
import { IconButton, Tooltip } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";

// ─── SSE utilities ────────────────────────────────────────────────────────────
import { generateUUID } from '../../../../utils/sseUtils';
import { SSE_BASE_URL } from '../../../../axios-order';
// ─────────────────────────────────────────────────────────────────────────────

export default function Gst2aReconciliation() {
  const history = useHistory();
  
  // State management
  const [loading, setLoading] = useState(false);

  // SSE states (replaces polling states)
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  
  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  
  // SSE ref — persists across renders without causing re-renders
  const eventSourceRef = useRef(null);
  const sseOpenedRef = useRef(false);

  // ─── Cleanup SSE on unmount ───────────────────────────────────────────────
  useEffect(() => {
    getReconDetailsAndRedirect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  const handleSnackbarOpen = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarSeverity("success");
  };

  const handleBack = () => {
    history.push('/reconCards');
  };

  const redirectToReport = (reconId) => {
    const encrypt = (text) => {
      return CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(text));
    };
    
    history.push({
      pathname: `/gst2aReconReport/${encrypt(reconId)}`,
      data: reconId,
    });
  };

  // ─── SSE helper ──────────────────────────────────────────────────────────
  const initiateReconSSE = (sseToken) => {
    return new Promise((resolve, reject) => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      console.log("SSE Starting point");
      sseOpenedRef.current = false;

      const sseUrl = `${SSE_BASE_URL}/api/sse/${sseToken}`;
      const es = new EventSource(sseUrl);
      eventSourceRef.current = es;

      console.log("SSE object created:", sseUrl, "readyState:", es.readyState);

      es.onopen = () => {
        sseOpenedRef.current = true;
        console.log('SSE onopen fired, readyState:', es.readyState);
        resolve();
      };

      es.onmessage = (event) => {
        console.log("RAW SSE:", event.data);
        try {
          const data = JSON.parse(event.data);
          console.log("REAL SSE DATA:", data);
          if (data.status === "done" || data.status === "Pass") {
            console.log("Recon completed via SSE, recon_id:", data.recon_id);
            if (eventSourceRef.current) {
              eventSourceRef.current.close();
              eventSourceRef.current = null;
            }
            // ── VERIFY: check backend log for exact field name ──
            const reconId = data.recon_id || data.gstr2a_recon_id;
            fetchAndRedirect(reconId);
          }
        } catch (err) {
          console.log("Ignoring non-json SSE message:", event.data);
        }
      };

      es.onerror = (e) => {
        const rs = eventSourceRef.current?.readyState;
        console.log("SSE onerror fired, readyState:", rs, "timestamp:", new Date().toISOString());
        if (rs === 2) {
          console.log("SSE permanently closed (readyState 2)");
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
    }
          if (!sseOpenedRef.current) {
            reject(new Error("SSE connection failed permanently"));
    }
        }
      };
    });
  };

  // ─── Fetch report and redirect ────────────────────────────────────────────
  const fetchAndRedirect = (reconId) => {
    console.log("fetchAndRedirect called with:", reconId);
    if (!reconId) {
      setIsProcessing(false);
      setProcessingMessage('');
      setLoading(false);
      handleSnackbarOpen('No recon ID received from SSE.', 'error');
      return;
    }

    axios
      .get(`/gstr2a/summary/${reconId}`)
      .then((res) => {
        if (res.data.status && res.data.record) {
          setIsProcessing(false);
          setProcessingMessage('');
          setLoading(false);
          handleSnackbarOpen("GSTR-2A Reconciliation completed! Redirecting...", "success");
          setTimeout(() => {
            redirectToReport(reconId);
          }, 1000);
        } else {
          setIsProcessing(false);
          setProcessingMessage('');
          setLoading(false);
          handleSnackbarOpen('Failed to fetch report. Please refresh.', 'error');
        }
      })
      .catch(() => {
        setIsProcessing(false);
        setProcessingMessage('');
    setLoading(false);
        handleSnackbarOpen('Error loading report data.', 'error');
      });
  };

  const checkReconciliationAndRedirect = (reconId) => {
    axios
      .get(`/gstr2a/summary/${reconId}`)
      .then((res) => {
        if (res.data.status && res.data.record) {
          const record = res.data.record;

          if (record.gstSummaryDashBoard || Object.keys(record).length > 3) {
            handleSnackbarOpen("Loading report...", "success");
            setLoading(false);
            setTimeout(() => {
              redirectToReport(reconId);
            }, 500);
          } else {
            // Still processing — redirect, report page will handle it
            handleSnackbarOpen("Processing reconciliation... Please wait.", "info");
            setLoading(false);
            redirectToReport(reconId);
          }
        } else {
          setLoading(false);
          handleSnackbarOpen("Unable to fetch reconciliation data", "warning");
        }
      })
      .catch((error) => {
        setLoading(false);
        handleSnackbarOpen(
          `Error: ${error?.response?.data || error.message}`,
          "error"
        );
      });
  };

  // ─── callUnifiedAutoRecon with SSE ────────────────────────────────────────
  const callUnifiedAutoRecon = async () => {
    try {
    setLoading(true);
      setIsProcessing(true);
      setProcessingMessage('Opening SSE connection...');
      handleSnackbarOpen("Initiating reconciliation...", "info");

      const sseToken = generateUUID();

      // STEP 1 — Wait until SSE actually opens
      await initiateReconSSE(sseToken);
      console.log('SSE confirmed open. Starting recon POST.');
      setProcessingMessage('Reconciliation started. Waiting for result...');

      const requestPayload = {
        report_type: "gstr2a",
        org_gstin: localStorage.getItem("gst"),
        business_entity_id: localStorage.getItem("business_entity_id"),
        period_type: "current_month",
        threshold_value: 5,
        sse_token: sseToken,
        functionality: 'GSTR2A Recon', // ── VERIFY against backend log
      };

      // STEP 2 — Start recon only AFTER SSE is open
      const res = await axios.post(`/unified_auto_reconciliation`, requestPayload);
      console.log('Recon API response:', res.data);
            
      if (!res.data || res.data.status === false) {
        if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
        setIsProcessing(false);
        setProcessingMessage('');
              setLoading(false);
        handleSnackbarOpen(res.data?.message || "Failed to initiate reconciliation", "error");
        return;
      }

      handleSnackbarOpen("Reconciliation initiated. Waiting for result...", "info");
      // Result arrives via SSE → fetchAndRedirect → redirectToReport

    } catch (error) {
      console.error('Error in callUnifiedAutoRecon:', error);
      if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
      setIsProcessing(false);
      setProcessingMessage('');
          setLoading(false);
          handleSnackbarOpen(
        error?.response?.data?.message || error.message || "Failed to initiate reconciliation",
            "error"
          );
    }
  };

  const getReconDetailsAndRedirect = () => {
    setLoading(true);
    
    axios
      .post(`/get_latest_recon_records`, {
        report_type: "gstr2a",
        org_gstin: localStorage.getItem("gst"),
        business_entity_id: localStorage.getItem("business_entity_id")
      })
      .then((res) => {
        if (!res.data) {
          handleSnackbarOpen("No reconciliation data available, initiating new reconciliation...", "info");
          callUnifiedAutoRecon();
          return;
        }

        let reconId = null;
        let foundLocation = "not found";
        
        if (res.data.recon_id) {
          reconId = res.data.recon_id;
          foundLocation = "res.data.recon_id";
        } else if (res.data.gstr2a_recon_id) {
          reconId = res.data.gstr2a_recon_id;
          foundLocation = "res.data.gstr2a_recon_id";
        } else if (res.data.record?.recon_id) {
          reconId = res.data.record.recon_id;
          foundLocation = "res.data.record.recon_id";
        } else if (res.data.record?.gstr2a_recon_id) {
          reconId = res.data.record.gstr2a_recon_id;
          foundLocation = "res.data.record.gstr2a_recon_id";
        } else if (res.data.record?.id) {
          reconId = res.data.record.id;
          foundLocation = "res.data.record.id";
        } else if (res.data.data?.recon_id) {
          reconId = res.data.data.recon_id;
          foundLocation = "res.data.data.recon_id";
        }
        
        const isValidReconId = (
          reconId !== null && 
          reconId !== undefined && 
          reconId !== "" && 
          reconId !== 0 &&
          reconId !== "null" &&
          reconId !== "undefined" &&
          String(reconId).trim() !== ""
        );

        if (isValidReconId) {
          handleSnackbarOpen("Loading existing reconciliation...", "info");
          checkReconciliationAndRedirect(reconId);
        } else {
          handleSnackbarOpen("No existing reconciliation found. Initiating new reconciliation...", "info");
          callUnifiedAutoRecon();
        }
      })
      .catch((error) => {
        handleSnackbarOpen("Error fetching records. Initiating new reconciliation...", "warning");
        callUnifiedAutoRecon();
    });
  };

  return (
    <React.Fragment>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: '20px'
      }}>
        <Tooltip title="Back" arrow>
          <IconButton
            onClick={handleBack}
            sx={{
              position: 'absolute',
              top: '20px',
              left: '20px',
              borderRadius: '50%',
              padding: '6px',
              color: '#263238',
              fontSize: '20px',
              backgroundColor: '#ffffff',
              height: '36px',
              width: '36px',
              '&:hover': {
                backgroundColor: '#455a64',
                color: '#ffffff',
              },
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
              transition: 'background-color 0.3s ease',
            }}
          >
            <ArrowBackIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </Tooltip>

        {loading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px'
          }}>
            <CircularProgress size={60} />
            <div style={{
              textAlign: 'center',
              fontSize: '18px',
              color: '#263238'
            }}>
              {isProcessing
                ? processingMessage || "Processing GSTR-2A Reconciliation..."
                : "Loading GSTR-2A Reconciliation Report..."}
            </div>
            <div style={{
              fontSize: '14px',
              color: '#666',
              textAlign: 'center',
              maxWidth: '400px'
            }}>
              Please wait while we prepare your reconciliation report. This may take a few moments.
            </div>
                          </div>
                        ) : (
          <div style={{
            textAlign: 'center',
            fontSize: '16px',
            color: '#666'
          }}>
            If you are not redirected automatically, please go back and try again.
                          </div>
                        )}
      </div>

      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
                    }}
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </React.Fragment>
  );
}